import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  NumberFieldProps,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps,
  TextareaFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { DynamicFieldRepository } from '@/store/dynamic-form-state/dynamic-field-state/dynamic-field.repository';
import { DynamicFieldType, DynaStatus, FormType } from '@/types';
import { capitalize } from 'lodash';
import { map, Observable } from 'rxjs';

interface getDynamicFieldFormObjectProps {
  store: DynamicFieldRepository;
  formType?: Observable<FormType>;
}

export const getDynamicFieldFormObject = ({ store, formType }: getDynamicFieldFormObjectProps): DynamicForm => {
  const nameField: DynamicField<TextFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'name'),
    props: {
      value: store.getNestedObservable<string>('dto', 'name'),
      onChange: (value: string) => {
        store.setNested('dto', 'name', value);
        store.clearError('name');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.name',
    },
  };

  const labelField: DynamicField<TextFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'label'),
    props: {
      value: store.getNestedObservable<string>('dto', 'label'),
      onChange: (value: string) => {
        store.setNested('dto', 'label', value);
        store.clearError('label');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.label',
    },
  };

  const typeField: DynamicField<SelectFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.type',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'type'),
    props: {
      value: store.getObservable('typeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('typeSelected', value);
        store.setNested('dto', 'type', value?.code);
        store.clearError('type');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.type',
      options: Object.entries(DynamicFieldType).map(([key, value]) => ({
        code: DynamicFieldType[key as keyof typeof DynamicFieldType],
        name: capitalize(value.toString()),
      })),
    },
  };

  const descriptionField: DynamicField<TextareaFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.description',
    variant: FieldVariant.TEXTAREA,
    props: {
      value: store.getNestedObservable<string>('dto', 'description'),
      onChange: (value: string) => {
        store.setNested('dto', 'description', value);
        store.clearError('description');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.description',
      rows: 5,
      resize: 'none',
    },
  };

  const isRequiredField: DynamicField<SwitchFieldProps> = {
    label: 'Champ obligatoire',
    variant: FieldVariant.SWITCH,
    props: {
      checked: store.getNestedObservable<boolean>('dto', 'isRequired'),
      onChange: value => {
        store.setNested('dto', 'isRequired', value.checked, false);
        store.clearError('isRequired');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.isRequired',
    },
  };

  const statusField: DynamicField<SwitchFieldProps> = {
    label: 'transanctionalForm.form.status',
    variant: FieldVariant.SWITCH,
    props: {
      checked: store.getNestedObservable<DynaStatus>('dto', 'status').pipe(map(value => value === DynaStatus.ACTIF)),
      onChange: value => {
        store.setNested('dto', 'status', value.checked ? DynaStatus.ACTIF : DynaStatus.NON_ACTIF);
        store.clearError('status');
      },
      placeholder: 'transanctionalForm.form.placeholders.status',
    },
  };

  const hasTotalField: DynamicField<SwitchFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.hasTotal',
    variant: FieldVariant.SWITCH,
    isHidden: formType?.pipe(map(value => value === FormType.FORM)),
    props: {
      checked: store.getNestedObservable<boolean>('dto', 'hasTotal'),
      onChange: value => {
        store.setNested('dto', 'hasTotal', value.checked, false);
        store.setNested('dto', 'formula', '');
        store.setNested('dto', 'isFormula', false, false);
        store.clearError('hasTotal');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.hasTotal',
    },
  };

  const isFormulaField: DynamicField<SwitchFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.isFormula',
    variant: FieldVariant.SWITCH,
    isHidden: formType?.pipe(map(value => value === FormType.FORM)),
    props: {
      checked: store.getNestedObservable<boolean>('dto', 'isFormula'),
      onChange: value => {
        store.setNested('dto', 'isFormula', value.checked, false);
        store.setNested('dto', 'formula', '');
        store.setNested('dto', 'hasTotal', false, false);
        store.clearError('isFormula');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.isFormula',
    },
  };

  const formulaField: DynamicField<TextFieldProps> = {
    label: 'transanctionalForm.dynamicField.form.formula',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isHidden: store.getNestedObservable<boolean>('dto', 'isFormula').pipe(map(value => !value)),
    error: store.getNestedObservable<string>('errors', 'formula'),
    props: {
      value: store.getNestedObservable<string>('dto', 'formula'),
      onChange: (value: string) => {
        store.setNested('dto', 'formula', value);
        store.clearError('formula');
      },
      placeholder: 'transanctionalForm.dynamicField.form.placeholders.formula',
    },
  };

  const orderField: DynamicField<NumberFieldProps> = {
    label: 'transanctionalForm.form.order',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'order'),
    props: {
      value: store.getNestedObservable<number>('dto', 'order'),
      onChange: (value: number) => {
        store.setNested('dto', 'order', value);
        store.clearError('order');
      },
      placeholder: 'transanctionalForm.form.placeholders.order',
    },
  };

  return {
    title: 'Formulaire dynamique',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [nameField, labelField],
          },
          {
            fields: [typeField, orderField],
          },
          {
            fields: [descriptionField],
          },
          {
            fields: [isRequiredField, statusField],
          },
          {
            fields: [isFormulaField, hasTotalField],
          },
          {
            fields: [formulaField],
          },
        ],
      },
    ],
  };
};
